import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { ImportColumnConfig } from '../interfaces/import-config.interface';

@Injectable({
  providedIn: 'root'
})
export class ExcelGeneratorService {

  /**
   * Generates an Excel file from validated data
   * @param data Array of validated data objects
   * @param columns Column configuration
   * @param fileName Original file name or custom name
   * @param sheetName Name for the Excel sheet
   * @returns Promise<File> - Generated Excel file
   */
  async generateExcelFile(
    data: any[], 
    columns: ImportColumnConfig[], 
    fileName: string = 'import.xlsx',
    sheetName: string = 'Datos'
  ): Promise<File> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    // Add headers based on column configuration
    const headers = columns.map(col => col.excelColumnName || col.displayName);
    worksheet.addRow(headers);

    // Style headers
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Add data rows
    data.forEach(row => {
      const rowData = columns.map(col => {
        const value = row[col.key];
        return this.formatCellValue(value, col.dataType);
      });
      worksheet.addRow(rowData);
    });

    // Auto-fit columns
    columns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(
        col.displayName.length + 2,
        this.calculateColumnWidth(data, col.key)
      );
    });

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create File object
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    return new File([blob], fileName, { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  /**
   * Generates Excel file from multiple sheets data
   * @param sheetsData Array of sheet data with names and data
   * @param columns Column configuration
   * @param fileName File name
   * @returns Promise<File> - Generated Excel file with multiple sheets
   */
  async generateMultiSheetExcelFile(
    sheetsData: { name: string; data: any[] }[],
    columns: ImportColumnConfig[],
    fileName: string = 'import.xlsx'
  ): Promise<File> {
    const workbook = new ExcelJS.Workbook();

    for (const sheetData of sheetsData) {
      const worksheet = workbook.addWorksheet(sheetData.name);

      // Add headers
      const headers = columns.map(col => col.excelColumnName || col.displayName);
      worksheet.addRow(headers);

      // Style headers
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };

      // Add data rows
      sheetData.data.forEach(row => {
        const rowData = columns.map(col => {
          const value = row[col.key];
          return this.formatCellValue(value, col.dataType);
        });
        worksheet.addRow(rowData);
      });

      // Auto-fit columns
      columns.forEach((col, index) => {
        const column = worksheet.getColumn(index + 1);
        column.width = Math.max(
          col.displayName.length + 2,
          this.calculateColumnWidth(sheetData.data, col.key)
        );
      });
    }

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create File object
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    return new File([blob], fileName, { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  /**
   * Formats cell value based on data type
   * @param value The cell value
   * @param dataType The expected data type
   * @returns Formatted value
   */
  private formatCellValue(value: any, dataType: string): any {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    switch (dataType) {
      case 'number':
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      case 'date':
        return value instanceof Date ? value : new Date(value);
      case 'boolean':
        return Boolean(value);
      case 'string':
      default:
        return String(value);
    }
  }

  /**
   * Calculates optimal column width based on data
   * @param data Array of data objects
   * @param key Property key to check
   * @returns Optimal column width
   */
  private calculateColumnWidth(data: any[], key: string): number {
    let maxLength = 10; // minimum width
    
    data.forEach(row => {
      const value = row[key];
      if (value !== null && value !== undefined) {
        const length = String(value).length;
        if (length > maxLength) {
          maxLength = length;
        }
      }
    });

    // Cap maximum width
    return Math.min(maxLength + 2, 50);
  }

  /**
   * Creates an Excel file with validation errors highlighted
   * @param data Array of data with validation errors
   * @param columns Column configuration
   * @param errors Array of validation errors
   * @param fileName File name
   * @returns Promise<File> - Excel file with error highlighting
   */
  async generateExcelWithErrors(
    data: any[],
    columns: ImportColumnConfig[],
    errors: { row: number; column: string; message: string }[],
    fileName: string = 'import_with_errors.xlsx'
  ): Promise<File> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Datos con Errores');

    // Add headers
    const headers = columns.map(col => col.excelColumnName || col.displayName);
    worksheet.addRow(headers);

    // Style headers
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Add data rows
    data.forEach((row, rowIndex) => {
      const rowData = columns.map(col => {
        const value = row[col.key];
        return this.formatCellValue(value, col.dataType);
      });
      const excelRow = worksheet.addRow(rowData);

      // Highlight cells with errors
      const rowErrors = errors.filter(error => error.row === rowIndex);
      rowErrors.forEach(error => {
        const columnIndex = columns.findIndex(col => col.key === error.column);
        if (columnIndex !== -1) {
          const cell = excelRow.getCell(columnIndex + 1);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFE0E0' } // Light red background
          };
          cell.note = error.message; // Add comment with error message
        }
      });
    });

    // Auto-fit columns
    columns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(
        col.displayName.length + 2,
        this.calculateColumnWidth(data, col.key)
      );
    });

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create File object
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    return new File([blob], fileName, { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  /**
   * Creates a template Excel file based on column configuration
   * @param columns Column configuration
   * @param fileName File name
   * @returns Promise<File> - Template Excel file
   */
  async generateTemplateFile(
    columns: ImportColumnConfig[],
    fileName: string = 'template.xlsx'
  ): Promise<File> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    // Add headers
    const headers = columns.map(col => col.excelColumnName || col.displayName);
    const headerRow = worksheet.addRow(headers);

    // Style headers
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4472C4' } // Blue background
    };
    headerRow.font.color = { argb: 'FFFFFFFF' }; // White text

    // Add sample data row
    const sampleData = columns.map(col => {
      switch (col.dataType) {
        case 'number':
          return col.key.includes('latitud') ? '14.0842883' : 
                 col.key.includes('longitud') ? '-87.1884333' : '123';
        case 'date':
          return new Date().toISOString().split('T')[0];
        case 'boolean':
          return 'true';
        case 'string':
        default:
          return `Ejemplo ${col.displayName}`;
      }
    });
    
    const sampleRow = worksheet.addRow(sampleData);
    sampleRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' } // Light gray background
    };

    // Auto-fit columns and add notes for required fields
    columns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(col.displayName.length + 2, 15);
      
      if (col.required) {
        const cell = headerRow.getCell(index + 1);
        cell.note = 'Campo requerido';
      }
    });

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();
    
    // Create File object
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    return new File([blob], fileName, { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }
}